import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { get, post } from "../../utils/http";
import {
  PERMISSION_MAP,
  PERMISSION_TAG_COLOR,
  PERMISSIONS,
  STATUS,
  STATUS_MAP,
  STATUS_TAG_COLOR,
} from "../../utils/consts";

import {
  Tabs,
  Button,
  Card,
  Row,
  Col,
  Image,
  Typography,
  Divider,
  Tag,
  Space,
  Modal,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  AudioOutlined,
  EditOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import DialogTable from "./components/DialogTable";
import WordTable from "./components/WordTable";
import PhraseTable from "./components/PhraseTable";
import CreateModal from "../../components/scene/createModal";
import { DialogData, WordData, PhraseData } from "./types";

const { Title, Text, Paragraph } = Typography;

interface SceneData {
  id: string | number;
  scene_name: string;
  scene_name_en: string;
  scene_desc: string;
  module_name: string;
  module_id: number;
  permission_type: number;
  scene_status: number;
  main_pic: string;
  dialog_pic: string;
  is_recommend: boolean;
  recommend_text_zh: string;
  recommend_text_en: string;
}

function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [sceneData, setSceneData] = useState<SceneData | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dialog");
  const [showScene, setShowScene] = useState(false);
  const [editingScene, setEditingScene] = useState<SceneData | null>(null);

  const [dialogData, setDialogData] = useState<DialogData[]>([]);
  const [wordData, setWordData] = useState<WordData[]>([]);
  const [phraseData, setPhraseData] = useState<PhraseData[]>([]);

  useEffect(() => {
    if (id) {
      fetchSceneDetail(id);
    }
  }, [id]);

  const fetchSceneDetail = async (sceneId: string) => {
    setLoading(true);
    try {
      const response = await get("/api/manage/scene-content", {
        scene_id: sceneId,
      });
      console.log("🚀 ~ fetchSceneDetail ~ response:", response);
      if (response?.data?.data?.scene) {
        setSceneData(response.data.data.scene);
      }
      // 确保始终赋值数组给状态变量
      setDialogData(
        Array.isArray(response?.data?.data?.sentences)
          ? response.data.data.sentences
          : [],
      );
      setWordData(
        Array.isArray(response?.data?.data?.words)
          ? response.data.data.words
          : [],
      );
      setPhraseData(
        Array.isArray(response?.data?.data?.phrases)
          ? response.data.data.phrases
          : [],
      );
    } catch (error) {
      console.error("🚀 ~ fetchSceneDetail ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!sceneData) return;
    setEditingScene(sceneData);
    setShowScene(true);
  };

  const closeModalCb = () => {
    setEditingScene(null);
    setShowScene(false);
    if (id) {
      fetchSceneDetail(id);
    }
  };

  if (loading) {
    return <div>加载中...</div>;
  }

  if (!sceneData) {
    return <div>暂无数据</div>;
  }

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 24 }}
      >
        返回
      </Button>

      <Card
        title={
          <Title level={3} style={{ margin: 0 }}>
            场景详情
          </Title>
        }
        extra={
          <Button icon={<EditOutlined />} onClick={handleEdit}>
            编辑
          </Button>
        }
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              场景名称
            </Text>
            <Paragraph style={{ marginBottom: 16 }}>
              {sceneData.scene_name}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              场景名称翻译
            </Text>
            <Paragraph style={{ marginBottom: 16 }}>
              {sceneData.scene_name_en}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              所属模块
            </Text>
            <Paragraph style={{ marginBottom: 16 }}>
              {sceneData.module_name}
            </Paragraph>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              所属权限
            </Text>
            <Tag
              color={
                PERMISSION_TAG_COLOR[sceneData.permission_type as PERMISSIONS]
              }
            >
              {PERMISSION_MAP[sceneData.permission_type as PERMISSIONS]}
            </Tag>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              状态
            </Text>
            <Tag color={STATUS_TAG_COLOR[sceneData.scene_status as STATUS]}>
              {STATUS_MAP[sceneData.scene_status as STATUS]}
            </Tag>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              是否推荐
            </Text>
            <Tag color={sceneData.is_recommend ? "green" : "default"}>
              {sceneData.is_recommend ? "是" : "否"}
            </Tag>
          </Col>
          {sceneData.is_recommend && (
            <>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  推荐语-中文
                </Text>
                <Paragraph style={{ marginBottom: 16 }}>
                  {sceneData.recommend_text_zh || "-"}
                </Paragraph>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  推荐语-英文
                </Text>
                <Paragraph style={{ marginBottom: 16 }}>
                  {sceneData.recommend_text_en || "-"}
                </Paragraph>
              </Col>
            </>
          )}
          <Col xs={24}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              场景描述
            </Text>
            <Paragraph style={{ marginBottom: 16 }}>
              {sceneData.scene_desc}
            </Paragraph>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              封面图片
            </Text>
            {sceneData.main_pic ? (
              <Image
                src={sceneData.main_pic}
                alt="封面图片"
                style={{ width: "100px", maxWidth: 400 }}
              />
            ) : (
              <Text type="secondary">暂无封面图片</Text>
            )}
          </Col>
          <Col xs={24} sm={12}>
            <Text strong style={{ display: "block", marginBottom: 8 }}>
              对话背景图
            </Text>
            {sceneData.dialog_pic ? (
              <Image
                src={sceneData.dialog_pic}
                alt="对话背景图"
                style={{ width: "100px", maxWidth: 400 }}
              />
            ) : (
              <Text type="secondary">暂无对话背景图</Text>
            )}
          </Col>
        </Row>
      </Card>

      <Card
        title="场景内容"
        extra={
          <Space>
            <Button
              type="primary"
              onClick={() => {
                if (!id) {
                  message.error("场景ID不存在");
                  return;
                }
                Modal.confirm({
                  title: "确认生成对话",
                  content: "确定要重新生成该场景的对话吗？",
                  onOk: async () => {
                    try {
                      const response = await post(
                        "/api/manage/generate-dialog",
                        {
                          scene_id: id,
                        },
                      );
                      if (response?.data?.code === 0) {
                        message.success(response?.data?.data?.message);
                      } else {
                        message.error("对话生成失败");
                      }
                    } catch (error) {
                      message.error("对话生成失败");
                    }
                  },
                });
              }}
            >
              生成对话
            </Button>
            <Button
              icon={<AudioOutlined />}
              onClick={() => {
                if (!id) {
                  message.error("场景ID不存在");
                  return;
                }
                Modal.confirm({
                  title: "确认生成全部语音",
                  content: "确定要重新生成该场景的全部语音吗？",
                  onOk: async () => {
                    try {
                      const response = await post(
                        "/api/manage/batch-generate-voice",
                        {
                          scene_id: id,
                        },
                      );
                      if (response?.data?.code === 0) {
                        message.success(response?.data?.data?.message);
                      } else {
                        message.error("语音生成失败");
                      }
                    } catch (error) {
                      message.error("全部语音生成失败");
                    }
                  },
                });
              }}
            >
              生成全部语音
            </Button>
            <Button
              onClick={() => {
                if (!id) {
                  message.error("场景ID不存在");
                  return;
                }
                let textareaRef: HTMLTextAreaElement | null = null;
                Modal.confirm({
                  title: "批量导入对话",
                  width: 600,
                  content: (
                    <div style={{ padding: "8px 0" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                        }}
                      >
                        <Text strong style={{ fontSize: 14, color: "#666" }}>
                          格式要求（JSON数组）
                        </Text>
                        <Button
                          icon={<CopyOutlined />}
                          size="small"
                          onClick={() => {
                            const jsonExample = JSON.stringify(
                              [
                                { en: "hello", zh: "你好", role: "1" },
                                { en: "hi", zh: "嗨", role: "2" },
                              ],
                              null,
                              2,
                            );
                            navigator.clipboard
                              .writeText(jsonExample)
                              .then(() => {
                                message.success("JSON格式已复制");
                              });
                          }}
                        >
                          复制格式
                        </Button>
                      </div>
                      <textarea
                        ref={(ref) => (textareaRef = ref)}
                        style={{
                          width: "100%",
                          height: 200,
                          padding: 12,
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          resize: "vertical",
                          fontSize: 13,
                          lineHeight: 1.6,
                          fontFamily: "'Monaco', 'Menlo', monospace",
                          boxSizing: "border-box",
                          backgroundColor: "#fafafa",
                        }}
                        placeholder={`[\n  { "en": "hello", "zh": "你好", "role": "1" },\n  { "en": "hi", "zh": "嗨", "role": "2" }\n]\n\n说明：role 取值为 1（男性）或 2（女性）`}
                      />
                    </div>
                  ),
                  onOk: async () => {
                    try {
                      const content = textareaRef?.value || "";
                      const response = await post(
                        "/api/manage/scene-sentences-add",
                        {
                          scene_id: id,
                          sentences: content,
                        },
                      );
                      if (response?.data?.code === 0) {
                        message.success(
                          "对话导入成功，短语&单词正在生成中，请稍后查看",
                        );
                        // 刷新对话列表
                        fetchSceneDetail(id);
                      } else {
                        message.error("对话导入失败");
                      }
                    } catch (error) {
                      message.error("对话导入失败");
                    }
                  },
                });
              }}
            >
              批量导入对话
            </Button>
            <Button
              onClick={() => {
                if (!id) {
                  message.error("场景ID不存在");
                  return;
                }
                let textareaRef: HTMLTextAreaElement | null = null;
                Modal.confirm({
                  title: "批量导入场景内容",
                  width: 600,
                  content: (
                    <div style={{ padding: "8px 0" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 12,
                        }}
                      >
                        <Text strong style={{ fontSize: 14, color: "#666" }}>
                          格式要求（JSON对象）
                        </Text>
                        <Button
                          icon={<CopyOutlined />}
                          size="small"
                          onClick={() => {
                            const jsonExample = JSON.stringify(
                              {
                                dialog: [
                                  { en: "hello", zh: "你好", role: "1" },
                                  { en: "hi", zh: "嗨", role: "2" },
                                ],
                                words: [
                                  { en: "word", zh: "单词", sentence: "例句" },
                                ],
                                phrases: [
                                  {
                                    en: "phrase",
                                    zh: "短语",
                                    sentence: "例句",
                                  },
                                ],
                              },
                              null,
                              2,
                            );
                            navigator.clipboard
                              .writeText(jsonExample)
                              .then(() => {
                                message.success("JSON格式已复制");
                              });
                          }}
                        >
                          复制格式
                        </Button>
                      </div>
                      <textarea
                        ref={(ref) => (textareaRef = ref)}
                        style={{
                          width: "100%",
                          height: 260,
                          padding: 12,
                          border: "1px solid #d9d9d9",
                          borderRadius: 8,
                          resize: "vertical",
                          fontSize: 13,
                          lineHeight: 1.6,
                          fontFamily: "'Monaco', 'Menlo', monospace",
                          boxSizing: "border-box",
                          backgroundColor: "#fafafa",
                        }}
                        placeholder={`{\n  "dialog": [\n    { "en": "hello", "zh": "你好", "role": "1" },\n    { "en": "hi", "zh": "嗨", "role": "2" }\n  ],\n  "words": [\n    { "en": "word", "zh": "单词", "sentence": "例句" }\n  ],\n  "phrases": [\n    { "en": "phrase", "zh": "短语", "sentence": "例句" }\n  ]\n}\n\n说明：role 取值为 1（男性）或 2（女性），words/phrases 可选`}
                      />
                    </div>
                  ),
                  onOk: async () => {
                    try {
                      const raw = textareaRef?.value || "{}";
                      const content = JSON.parse(raw);
                      const response = await post(
                        "/api/manage/scene-content-import",
                        {
                          scene_id: id,
                          content,
                        },
                      );
                      if (response?.data?.code === 0) {
                        message.success("场景内容导入成功");
                        // 刷新场景内容
                        fetchSceneDetail(id);
                      } else {
                        message.error("场景内容导入失败");
                      }
                    } catch (error) {
                      if (error instanceof SyntaxError) {
                        message.error("JSON 格式错误，请检查输入内容");
                      } else {
                        message.error("场景内容导入失败");
                      }
                    }
                  },
                });
              }}
            >
              批量导入场景内容
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "dialog",
              label: "对话",
              children: <DialogTable data={dialogData} sceneId={id || ""} />,
            },
            {
              key: "word",
              label: "单词",
              children: <WordTable data={wordData} sceneId={id || ""} />,
            },
            {
              key: "phrase",
              label: "短语",
              children: <PhraseTable data={phraseData} sceneId={id || ""} />,
            },
          ]}
        />
      </Card>

      <CreateModal
        data={editingScene}
        show={showScene}
        closeModalCb={closeModalCb}
      />
    </div>
  );
}

export default Detail;
